import {
  LBLabel,
  LBRadioGroup,
  LBRadioGroupItem,
  LBDateRangePicker
} from '@linearb/buzz';
import { IDateRange } from '@linearb/linjs-common';
import { SEARCH_DATE_FORMAT } from '@linearb/linui-utils';
import { ObjectValues } from 'common/utils/objectValues';
import startCase from 'lodash/startCase';
import moment from 'moment';

export const DATE_FILTERS = {
  UPDATED: 'updated',
  CREATED: 'created',
  DEPLOYED: 'deployed',
  CODING_ENDED: 'coding_ended',
  REVIEWED: 'reviewed',
  MERGED: 'merged',
  RELEASED: 'released',
  RELEASED_AT: 'released_at',
  CYCLE_TIME_UPDATED: 'cycle_time_updated',
  COMMITTED: 'committed',
  ISSUED_AT: 'issued_at',
  IN_PROGRESS_BETWEEN: 'in_progress_between',
  STARTED_AT: 'started_at',
  ENDED_AT: 'ended_at',
  // Used in team activity page
  TIME: 'time'
} as const;

export type DateFilter = ObjectValues<typeof DATE_FILTERS>;

export interface DateFilterValue {
  filterName: DateFilter;
  range: IDateRange;
}

interface DateFilterProps {
  range: IDateRange;
  selectedDateFilter: DateFilter;
  /**
   * List of date filters to render
   */
  filters: DateFilter[];
  filtersConfig: Record<string, any>;
  onChange(oldValue: DateFilterValue, newValue: DateFilterValue): void;
}

/**
 * Date filter component that allows selecting the date range and type of filter
 * Assumes only one type of filter can be picked at any time
 */
export function DateFilter({
  range,
  selectedDateFilter,
  onChange,
  filters = [],
  filtersConfig
}: DateFilterProps) {
  const { from, to } = range;
  const minDate = filtersConfig?.[selectedDateFilter]?.minDate();

  function handleSelectedDateFilterChange(value: DateFilter) {
    onChange(
      { range, filterName: selectedDateFilter as DateFilter },
      {
        range,
        filterName: value
      }
    );
  }

  function handleDateRangeChange(newRange: IDateRange) {
    onChange(
      { range, filterName: selectedDateFilter as DateFilter },
      { range: newRange, filterName: selectedDateFilter as DateFilter }
    );
  }

  const selected =
    from && to
      ? {
          from: moment(from, SEARCH_DATE_FORMAT).toDate(),
          to: moment(to, 'YYYY-MM-DD').toDate()
        }
      : undefined;

  return (
    <div className='mx-2 mt-4 flex flex-col gap-5'>
      <LBDateRangePicker
        mode='range'
        defaultMonth={moment().subtract(1, 'month').toDate()}
        disabled={[
          { after: new Date() },
          minDate ? { before: new Date(minDate) } : undefined
        ]}
        selected={selected}
        onSelect={selectedDates => {
          if (!selectedDates) {
            return;
          }

          handleDateRangeChange({
            from: selectedDates.from.toLocaleDateString('sv-SV', {
              dateStyle: 'short'
            }),
            to: selectedDates.to.toLocaleDateString('sv-SV', {
              dateStyle: 'short'
            })
          });
        }}
      />
      <LBRadioGroup
        onValueChange={handleSelectedDateFilterChange}
        value={selectedDateFilter}
      >
        {filters.map(filter => (
          <div key={filter} className='flex flex-row gap-4'>
            <LBRadioGroupItem id={filter} value={filter} />
            <LBLabel htmlFor={filter}>{startCase(filter)}</LBLabel>
          </div>
        ))}
      </LBRadioGroup>
    </div>
  );
}
